import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { ConfirmModal } from '../ui/ConfirmModal';
import { getAllHeroSlides, createHeroSlide, updateHeroSlide, deleteHeroSlide } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Upload, Link as LinkIcon, MoveUp, MoveDown } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HeroSlidesManager = () => {
  const { toast } = useToast();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, slide: null });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    buttonText: '',
    buttonUrl: '',
    order: 0,
    active: true
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  const fetchSlides = useCallback(async () => {
    try {
      const response = await getAllHeroSlides();
      setSlides(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch slides', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlide) {
        await updateHeroSlide(editingSlide.id, formData);
        toast({ title: 'Success', description: 'Slide updated successfully' });
      } else {
        await createHeroSlide(formData);
        toast({ title: 'Success', description: 'Slide created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSlides();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save slide', variant: 'destructive' });
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title,
      description: slide.description,
      image: slide.image,
      buttonText: slide.buttonText,
      buttonUrl: slide.buttonUrl,
      order: slide.order,
      active: slide.active
    });
    setImagePreview(slide.image);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteHeroSlide(deleteModal.slide.id);
      toast({ title: 'Success', description: 'Slide deleted successfully' });
      fetchSlides();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete slide', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (slide) => {
    try {
      await updateHeroSlide(slide.id, { active: !slide.active });
      toast({ title: 'Success', description: `Slide ${slide.active ? 'deactivated' : 'activated'}` });
      fetchSlides();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update slide', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image: '', buttonText: '', buttonUrl: '', order: 0, active: true });
    setEditingSlide(null);
    setImagePreview('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'image') {
      setImagePreview(e.target.value);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      
      setFormData({ ...formData, image: imageUrl });
      setImagePreview(imageUrl);
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">Hero Slides</h1>
          <p className="text-gray-400 mt-2">Manage your hero section slider</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
              <Plus className="w-4 h-4 mr-2" /> Add Slide
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-black border-orange-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{editingSlide ? 'Edit Slide' : 'Add New Slide'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input name="title" value={formData.title} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea name="description" value={formData.description} onChange={handleChange} required rows={3} className="bg-black/50 border-green-500/30 resize-none" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Image</label>
                <Tabs defaultValue="url" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-black/50">
                    <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
                    <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
                  </TabsList>
                  <TabsContent value="url" className="space-y-2">
                    <Input 
                      name="image" 
                      value={formData.image} 
                      onChange={handleChange} 
                      placeholder="https://example.com/image.jpg"
                      className="bg-black/50 border-green-500/30" 
                    />
                  </TabsContent>
                  <TabsContent value="upload" className="space-y-2">
                    <Input 
                      type="file" 
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="bg-black/50 border-green-500/30" 
                    />
                    {uploading && <p className="text-sm text-orange-400">Uploading...</p>}
                  </TabsContent>
                </Tabs>
                {imagePreview && (
                  <div className="mt-2">
                    <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded border border-green-500/30" />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Button Text</label>
                  <Input name="buttonText" value={formData.buttonText} onChange={handleChange} required className="bg-black/50 border-green-500/30" placeholder="Explore Now" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Button URL</label>
                  <Input name="buttonUrl" value={formData.buttonUrl} onChange={handleChange} required className="bg-black/50 border-green-500/30" placeholder="/releases" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Order</label>
                  <Input name="order" type="number" value={formData.order} onChange={handleChange} className="bg-black/50 border-green-500/30" />
                </div>
                <div className="flex items-center space-x-2 pt-7">
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    className="data-[state=checked]:bg-green-500"
                  />
                  <label className="text-sm font-medium">Active</label>
                </div>
              </div>

              <Button type="submit" disabled={uploading} className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
                {editingSlide ? 'Update Slide' : 'Create Slide'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {slides.map((slide) => (
          <Card key={slide.id} className="bg-black/50 border-orange-500/30 overflow-hidden group p-0">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-3 left-3 z-10 flex gap-2">
                <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Order: {slide.order}</span>
                <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${slide.active ? 'bg-green-500' : 'bg-gray-500'}`}>
                  {slide.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="p-4 pb-6">
              <h3 className="text-lg font-bold text-white mb-1">{slide.title}</h3>
              <p className="text-gray-400 text-sm mb-2 line-clamp-2">{slide.description}</p>
              <p className="text-gray-500 text-xs mb-3">Button: {slide.buttonText} → {slide.buttonUrl}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(slide)} className="flex-1 border-green-500/30 hover:bg-green-500/20">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleToggleActive(slide)} className="flex-1 border-orange-500/30 hover:bg-orange-500/20">
                  {slide.active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleteModal({ isOpen: true, slide })} className="flex-1 border-red-500/30 hover:bg-red-500/20 text-red-400">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, slide: null })}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the slide "${deleteModal.slide?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
